// User Management API Service - Backend Integration (Organizational)
import { apiGet, apiPost, apiPut, apiDelete } from '../services/apiClient';
import MockDataService from '../services/MockDataService';
import { masterRows } from '../utils/inquiryDisplay';
import { isRequesterRoleRecord } from '../utils/roleUtils';
import { getUsersWithRole } from './roleManagementService';

const USE_MOCK = process.env.REACT_APP_USE_MOCK_DATA === 'true' || process.env.NODE_ENV === 'development';

// Helper to wrap response in { data: ... } format for backward compatibility with axios-style responses
const wrapResponse = (data) => ({ data });

/**
 * Get users with pagination
 * Gateway path: /gateway/registration/api/signup/organization/users
 */
export const getUsers = async (params = {}) => {
  const response = await apiGet('/gateway/registration/api/signup/organization/users', params);
  return wrapResponse(response);
};

/**
 * Get agents (users with AGENT role) with pagination
 * Gateway path: /gateway/registration/api/signup/organization/agents
 */
export const getAgents = async (params = {}) => {
  const response = await apiGet('/gateway/registration/api/signup/organization/agents', params);
  return wrapResponse(response);
};

export const getUserDisplayName = (user) =>
  user?.fullName ||
  user?.username ||
  user?.email ||
  `User ${user?.userId ?? user?.id ?? ''}`.trim();

/** Requester roles never hold a ticket, so they are neither assignees nor responsible roles. */
export const isTicketHolderRole = (role) =>
  role?.roleId != null && !isRequesterRoleRecord(role);

const usersByRoleCache = new Map();

const fetchUsersForRole = (roleId) => {
  const key = String(roleId);
  if (!usersByRoleCache.has(key)) {
    usersByRoleCache.set(key, getUsersWithRole(roleId).then(masterRows).catch(() => []));
  }
  return usersByRoleCache.get(key);
};

/**
 * Active users of one role who can hold a ticket, from the same role->users endpoint the assign
 * flows use. Unlike `/agents` it is complete (no page size) and covers TEAM_LEAD/SUPERVISOR
 * assignees, not just the AGENT role.
 *
 * A role is required: returning [] instead of every role's users keeps report filters from firing
 * one request per role on mount, when nothing is filtered by assignee yet.
 *
 * Tenant scoping is the endpoint's job (RoleService#getUsersWithRole); only activity is filtered here.
 */
export const getAssignableUsers = async (roleId) => {
  if (roleId == null || roleId === '' || roleId === 'all') return [];

  const byId = new Map();
  (await fetchUsersForRole(Number(roleId))).forEach((user) => {
    const id = user?.userId ?? user?.id;
    if (id == null || user.isActive === false || user.isDeleted === true) return;
    const key = String(id);
    if (!byId.has(key)) byId.set(key, user);
  });
  return [...byId.values()].sort((a, b) =>
    getUserDisplayName(a).localeCompare(getUserDisplayName(b))
  );
};

/**
 * Get user by ID
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}
 */
export const getUserById = async (userId) => {
  if (USE_MOCK) {
    try {
      const response = await MockDataService.getUserById(userId);
      return wrapResponse(response.data);
    } catch (error) {
      const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}`);
      return wrapResponse(response);
    }
  }
  try {
    const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}`);
    return wrapResponse(response);
  } catch (error) {
    const response = await MockDataService.getUserById(userId);
    return wrapResponse(response.data);
  }
};

/**
 * Update user
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}
 */
export const updateUser = async (userId, data) => {
  const response = await apiPut(`/gateway/registration/api/signup/organization/users/${userId}`, data);
  return wrapResponse(response);
};

/**
 * Delete user
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}
 */
export const deleteUser = async (userId) => {
  const response = await apiDelete(`/gateway/registration/api/signup/organization/users/${userId}`);
  return wrapResponse(response);
};

/**
 * Get user roles
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}/roles
 */
export const getUserRoles = async (userId) => {
  if (USE_MOCK) {
    try {
      const response = await MockDataService.getUserRoles(userId);
      return wrapResponse(response.data);
    } catch (error) {
      const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}/roles`);
      return wrapResponse(response);
    }
  }
  try {
    const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}/roles`);
    return wrapResponse(response);
  } catch (error) {
    const response = await MockDataService.getUserRoles(userId);
    return wrapResponse(response.data);
  }
};

/**
 * Get user teams
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}/teams
 */
export const getUserTeams = async (userId) => {
  if (USE_MOCK) {
    try {
      const response = await MockDataService.getUserTeams(userId);
      return wrapResponse(response.data);
    } catch (error) {
      const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}/teams`);
      return wrapResponse(response);
    }
  }
  try {
    const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}/teams`);
    return wrapResponse(response);
  } catch (error) {
    const response = await MockDataService.getUserTeams(userId);
    return wrapResponse(response.data);
  }
};

/**
 * Assign department to user
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}/department
 */
export const assignDepartment = async (userId, departmentId) => {
  const response = await apiPut(`/gateway/registration/api/signup/organization/users/${userId}/department?departmentId=${departmentId}`, null);
  return wrapResponse(response);
};

/**
 * Assign manager to user
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}/manager
 */
export const assignManager = async (userId, managerId) => {
  const response = await apiPut(`/gateway/registration/api/signup/organization/users/${userId}/manager?managerId=${managerId}`, null);
  return wrapResponse(response);
};

/**
 * Activate user
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}/activate
 */
export const activateUser = async (userId) => {
  const response = await apiPost(`/gateway/registration/api/signup/organization/users/${userId}/activate`, null);
  return wrapResponse(response);
};

/**
 * Deactivate user
 * Gateway path: /gateway/registration/api/signup/organization/users/{userId}/deactivate
 */
export const deactivateUser = async (userId) => {
  const response = await apiPost(`/gateway/registration/api/signup/organization/users/${userId}/deactivate`, null);
  return wrapResponse(response);
};

/**
 * Bulk create users (JSON body)
 * POST /gateway/registration/api/signup/organization/users/bulk
 */
export const bulkCreateUsers = async (users) => {
  const response = await apiPost('/gateway/registration/api/signup/organization/users/bulk', {
    users,
  });
  return wrapResponse(response);
};

/**
 * Bulk import users (same backend logic as bulk create)
 * POST /gateway/registration/api/signup/organization/import/users
 */
export const bulkImportUsers = async (users) => {
  const response = await apiPost('/gateway/registration/api/signup/organization/import/users', {
    users,
  });
  return wrapResponse(response);
};

// Service object for backward compatibility
export const userManagementService = {
  getUsers,
  getAgents,
  getAssignableUsers,
  getUserDisplayName,
  isTicketHolderRole,
  getUserById,
  updateUser,
  deleteUser,
  getUserRoles,
  getUserTeams,
  assignDepartment,
  assignManager,
  activateUser,
  deactivateUser,
  bulkCreateUsers,
  bulkImportUsers,
};

export default userManagementService;
